import { useState, useEffect, useCallback } from "react";
import { apiGet } from "../api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { formatCurrency, formatMonthShort } from "../lib/format";

const CarriedBadge = ({ sourceMonth }) => (
  <span
    title={`Price carried forward from ${formatMonthShort(sourceMonth)} — no price file for this SKU in the invoice month`}
    style={{
      display: "inline-block", marginLeft: 5, padding: "0 4px", borderRadius: 4,
      fontSize: "0.58rem", fontWeight: 700, verticalAlign: "middle",
      background: "rgba(255,176,32,0.14)", color: "#FFB020", border: "1px solid rgba(255,176,32,0.35)",
    }}
  >
    ↩ {formatMonthShort(sourceMonth)}
  </span>
);

const SupplierOverchargeModal = ({ supplier, open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const loadData = useCallback(async () => {
    if (!supplier) return;
    setLoading(true);
    setError(false);
    setData(null);
    try {
      const result = await apiGet(`/dashboard/supplier-overcharges?supplier=${encodeURIComponent(supplier)}&limit=200`, { timeout: 60000 });
      if (!result) {
        throw new Error("No response from server");
      }
      setData(result);
    } catch (err) {
      console.error("Failed to load supplier overcharges:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [supplier]);

  useEffect(() => {
    if (open && supplier) {
      loadData();
    }
    if (!open) {
      setData(null);
    }
  }, [open, supplier, loadData]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent
        className="max-w-5xl"
        style={{
          background: "var(--card-bg, rgba(10, 18, 42, 0.98))",
          border: "1px solid rgba(96, 240, 248, 0.15)",
          backdropFilter: "blur(16px)",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          padding: 0,
        }}
        data-testid="supplier-overcharge-modal"
      >
        {/* Header — Shadcn's DialogContent auto-renders a close X in the
            top-right corner (see /app/frontend/src/components/ui/dialog.jsx
            line 37), so we deliberately do NOT add a second one here. */}
        <div style={{
          padding: "1.25rem 1.5rem",
          borderBottom: "1px solid rgba(96, 240, 248, 0.1)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          paddingRight: "3rem", // reserve space for the built-in close button
        }}>
          <DialogHeader style={{ padding: 0, margin: 0 }}>
            <DialogTitle style={{
              fontSize: "1.15rem", fontWeight: "700", color: "var(--octopus-ice)",
              display: "flex", alignItems: "center", gap: "0.5rem"
            }}>
              {supplier}
              <span style={{ fontSize: "0.75rem", fontWeight: "500", color: "var(--octopus-voltage)", opacity: 0.7 }}>
                — Overcharge Detail
              </span>
            </DialogTitle>
          </DialogHeader>
        </div>

        {/* Summary cards */}
        {!loading && data?.summary && (
          <div style={{
            padding: "1rem 1.5rem",
            display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.75rem",
            borderBottom: "1px solid rgba(96, 240, 248, 0.06)",
          }}>
            {[
              { label: "True Overcharge", value: formatCurrency(data.summary.total_overcharge), color: "var(--octopus-green)" },
              { label: "Overcharged Items", value: (data.summary.total_items || 0).toLocaleString(), color: "var(--octopus-pink)" },
              { label: "Avg Overcharge/Item", value: formatCurrency(data.summary.avg_overcharge), color: "var(--octopus-voltage)" },
              { label: "Likely Data Issues", value: `${formatCurrency(data.summary.data_issue_value || 0)} · ${(data.summary.data_issue_items || 0).toLocaleString()}`, color: "#FF9F0A" },
            ].map((s) => (
              <div key={s.label} style={{
                padding: "0.75rem 1rem", borderRadius: "8px",
                background: "rgba(96, 240, 248, 0.04)", border: "1px solid rgba(96, 240, 248, 0.08)"
              }}>
                <div style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.5px", color: s.color, marginBottom: "0.3rem" }}>
                  {s.label}
                </div>
                <div style={{ fontSize: "1.2rem", fontWeight: "700", color: "var(--octopus-ice)" }}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflow: "auto", padding: "0 1.5rem 1.5rem" }}>
          {loading && (
            <div style={{ padding: "3rem", textAlign: "center" }}>
              <div style={{
                width: 32, height: 32, border: "3px solid rgba(96, 240, 248, 0.15)",
                borderTopColor: "var(--octopus-ice)", borderRadius: "50%",
                animation: "spin 0.8s linear infinite", margin: "0 auto 1rem"
              }} />
              <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem" }}>Loading overcharge details...</p>
            </div>
          )}

          {error && (
            <div style={{ padding: "3rem", textAlign: "center" }}>
              <p style={{ color: "var(--octopus-pink)", marginBottom: "0.5rem" }}>Failed to load data</p>
              <button onClick={loadData} style={{
                background: "transparent", border: "1px solid var(--octopus-pink)",
                color: "var(--octopus-pink)", borderRadius: "6px", padding: "0.4rem 1rem",
                cursor: "pointer", fontSize: "0.8rem"
              }}>Retry</button>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {data.items.length === 0 ? (
                <div style={{ padding: "3rem", textAlign: "center", color: "var(--octopus-voltage)", opacity: 0.5 }}>
                  No overcharges found for this supplier
                </div>
              ) : (
                <table className="data-table" style={{ marginTop: "1rem" }} data-testid="overcharge-detail-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th style={{ textAlign: "right" }}>Invoice Price</th>
                      <th style={{ textAlign: "right" }}>Price File</th>
                      <th style={{ textAlign: "right" }}>Difference</th>
                      <th style={{ textAlign: "right" }}>Qty</th>
                      <th style={{ textAlign: "right" }}>Overcharge</th>
                      <th>Type</th>
                      <th>Date</th>
                      <th>Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item, idx) => (
                      <tr key={`${item.sku}-${item.invoice_ref}-${idx}`} data-testid={`overcharge-row-${idx}`}>
                        <td style={{ maxWidth: "220px" }}>
                          <div style={{ fontWeight: "600", color: "var(--octopus-ice)", fontSize: "0.82rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {item.sku || "—"}
                          </div>
                          <div style={{ fontSize: "0.72rem", color: "var(--octopus-voltage)", opacity: 0.7, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {item.description || ""}
                          </div>
                        </td>
                        <td style={{ textAlign: "right", fontWeight: "500", color: "var(--octopus-ice)", fontSize: "0.82rem" }}>
                          {formatCurrency(item.invoice_price)}
                        </td>
                        <td style={{ textAlign: "right", fontWeight: "500", color: "var(--octopus-green)", fontSize: "0.82rem" }}>
                          {formatCurrency(item.price_file_price)}
                          {item.price_basis === "carried_forward" && (
                            <CarriedBadge sourceMonth={item.price_source_month} />
                          )}
                        </td>
                        <td style={{ textAlign: "right", fontWeight: "600", color: "#F050F8", fontSize: "0.82rem" }}>
                          +{formatCurrency(item.difference)}
                        </td>
                        <td style={{ textAlign: "right", color: "var(--octopus-voltage)", fontSize: "0.82rem" }}>
                          {item.quantity}
                        </td>
                        <td style={{ textAlign: "right", fontWeight: "700", color: item.classification === "Likely data issue" ? "#FF9F0A" : "#F050F8", fontSize: "0.85rem" }}>
                          {formatCurrency(item.overcharge_amount)}
                        </td>
                        <td style={{ maxWidth: "200px" }}>
                          {item.classification === "Likely data issue" ? (
                            <>
                              <span style={{
                                fontSize: "0.6rem", padding: "0.1rem 0.4rem", borderRadius: "4px",
                                background: "rgba(255, 159, 10, 0.16)", color: "#FF9F0A", fontWeight: 700,
                                textTransform: "uppercase", letterSpacing: "0.03em", whiteSpace: "nowrap",
                              }}>Data issue</span>
                              {item.flag_reason && (
                                <div style={{ fontSize: "0.68rem", color: "var(--octopus-voltage)", opacity: 0.7, marginTop: "0.25rem", whiteSpace: "normal" }}>
                                  {item.flag_reason}
                                </div>
                              )}
                            </>
                          ) : (
                            <span style={{
                              fontSize: "0.6rem", padding: "0.1rem 0.4rem", borderRadius: "4px",
                              background: "rgba(48, 209, 88, 0.14)", color: "var(--octopus-green)", fontWeight: 700,
                              textTransform: "uppercase", letterSpacing: "0.03em", whiteSpace: "nowrap",
                            }}>True overcharge</span>
                          )}
                        </td>
                        <td style={{ color: "var(--octopus-voltage)", fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                          {item.invoice_date || "—"}
                        </td>
                        <td style={{ color: "var(--octopus-voltage)", fontSize: "0.78rem", whiteSpace: "nowrap", maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {item.invoice_ref || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {data.has_more && (
                <div style={{ textAlign: "center", padding: "1rem", color: "var(--octopus-voltage)", fontSize: "0.8rem", opacity: 0.7 }}>
                  Showing top {data.items.length} of {data.summary.total_items.toLocaleString()} overcharged items
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SupplierOverchargeModal;
